// ─── Window row rollup ──────────────────────────────────────────────────────
// Collapses identical mulled panes within the SAME mulled assembly into a
// single row with summed qty. This handles the "qty 2 of a 2-pane mulled
// DH+DH" case — instead of 4 separate rows of identical DH, we get one row
// of DH qty 4 inside that mulled unit.
//
// CRITICAL: Standalone (non-mulled) rows NEVER collapse with each other,
// even if their physical specs (size, color, style, glass) are identical.
// Two same-size standalone exterior doors are two separate items — they may
// differ in notes, sub-type, or other fields the rollup can't see, and the
// shop builds and bills them as two distinct units regardless.
//
// Rule: a row collapses with another row ONLY if BOTH of these are true:
//   1. Both rows are mulled panes (isMulledPane === true)
//   2. They share the SAME mulledUnitLabel (same physical assembly)
//   3. ALL of these physical specs match:
//      - twpType / style (the type — DH, PW, HS, CSMT, etc.)
//      - width / height
//      - colorOutside / colorInside
//      - glass / glassPkg
//      - flanges
//      - gridColonial / gridDiamond / gridPrairie
//      - screenHalf / screenFull
//      - handing
//
// Within mulled units, AJ's preferred row order is "PW first, then everything
// else". So when rolling up a mulled batch, we sort PW rows to the top.
// Standalone (non-mulled) rows keep their natural order.
//
// Adders pass through unchanged.

function norm(v) {
  if (v === null || v === undefined) return '';
  return String(v).trim().toLowerCase();
}

function nf(v) {
  const n = parseFloat(v);
  return isNaN(n) ? '' : String(n);
}

// Spec-only portion of the key — used INSIDE a single mulled assembly to
// detect identical panes that should sum quantities.
function specKey(w) {
  return [
    norm(w.twpType || w.style || w.description),
    nf(w.width),
    nf(w.height),
    norm(w.colorOutside),
    norm(w.colorInside),
    norm(w.glass || w.glassPkg),
    !!w.flanges,
    !!w.gridColonial,
    !!w.gridDiamond,
    !!w.gridPrairie,
    !!w.screenHalf,
    !!w.screenFull,
    norm(w.handing),
  ].join('|');
}

function isPW(w) {
  const t = norm(w.twpType || w.style || w.description);
  return t === 'pw' || t.startsWith('pw') || t.includes('picture');
}

function isAdder(w) {
  return w.lineType === 'adder' || w.type === 'adder';
}

function isMulledPane(w) {
  return !!w.isMulledPane;
}

export function rollupIdenticalWindows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];

  const adders = [];
  const standalone = [];   // non-mulled rows — never collapse with each other
  const mulledPanes = [];  // mulled rows — may collapse within their assembly

  rows.forEach((r, originalIdx) => {
    if (isAdder(r)) {
      adders.push({ row: r, originalIdx });
    } else if (isMulledPane(r)) {
      mulledPanes.push({ row: r, originalIdx });
    } else {
      standalone.push({ row: r, originalIdx });
    }
  });

  // ── Roll up mulled panes WITHIN each mulled assembly ──────────────────────
  // Key: `${mulledUnitLabel}::${specKey}` — only panes in the SAME assembly
  // with identical specs collapse together. Two separate mulled units never
  // merge with each other, even if their panes happen to match spec-for-spec.
  const mulledGroups = new Map(); // key → { row, qty, firstIdx, label }
  mulledPanes.forEach(({ row: w, originalIdx }) => {
    const label = norm(w.mulledUnitLabel) || `__nolabel_${originalIdx}`;
    const key = `${label}::${specKey(w)}`;
    const qty = parseFloat(w.qty) || 1;
    if (mulledGroups.has(key)) {
      mulledGroups.get(key).qty += qty;
    } else {
      mulledGroups.set(key, {
        row: { ...w },
        qty,
        firstIdx: originalIdx,
        label,
      });
    }
  });

  // Within each mulled assembly, sort PW rows to the top (AJ's preference).
  // Across assemblies, preserve original order via firstIdx.
  const mulledByLabel = new Map();
  for (const g of mulledGroups.values()) {
    if (!mulledByLabel.has(g.label)) mulledByLabel.set(g.label, []);
    mulledByLabel.get(g.label).push(g);
  }

  const rolledMulled = [];
  // Iterate label buckets in order of their earliest firstIdx so assembly
  // order matches the original input order.
  const labelOrder = [...mulledByLabel.entries()]
    .map(([label, items]) => ({
      label,
      earliestIdx: Math.min(...items.map(x => x.firstIdx)),
      items,
    }))
    .sort((a, b) => a.earliestIdx - b.earliestIdx);

  for (const { items } of labelOrder) {
    const sorted = items
      .map((g, i) => ({ g, i, pw: isPW(g.row) ? 0 : 1 }))
      .sort((a, b) => a.pw - b.pw || a.g.firstIdx - b.g.firstIdx)
      .map(x => ({ ...x.g.row, qty: x.g.qty, _origIdx: x.g.firstIdx }));
    rolledMulled.push(...sorted);
  }

  // ── Standalone rows pass through untouched ───────────────────────────────
  // Each one keeps its own row in the PDF, even if two standalones happen to
  // have matching specs. This is intentional: two same-size standalone doors
  // are two separate items.
  const standaloneOut = standalone.map(({ row, originalIdx }) => ({
    ...row,
    _origIdx: originalIdx,
  }));

  // ── Adders pass through unchanged, appended at the end ───────────────────
  const adderOut = adders.map(({ row }) => row);

  // ── Merge mulled + standalone preserving original input order ────────────
  // Adders always go last (matches previous behavior).
  const combined = [...rolledMulled, ...standaloneOut]
    .sort((a, b) => (a._origIdx ?? 0) - (b._origIdx ?? 0))
    .map(({ _origIdx, ...rest }) => rest);

  return [...combined, ...adderOut];
}
